import { useEffect, useState } from "react";
import axios from "axios";
import { baseUrl } from "../../../baseUrl";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../AuthContext";
import Swal from 'sweetalert2';
import TWPExamInstruction from "../TWPExamInstruction";
import FlexiExamInstruction from "../FlexiExamInstruction";
import { toast } from "react-toastify";
import api from "../../../api";

const DynamicExam = () => {
  const { examId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [exam, setExam] = useState(null);
  const [loading,setLoading] = useState(false)
  const [examStarted, setExamStarted] = useState(() => {
  return localStorage.getItem(`exam_${examId}_started`) === "true";
});
  const [read, setRead] = useState(false);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);

  const startExam = () => {
    if (!read) {
      Swal.fire({
        title: "Confirmation needed!",
        text: "Please confirm that you have read the instructions",
        icon: "warning",
      });
      return;
    }

    setExamStarted(true);
    localStorage.setItem(`exam_${examId}_started`, "true");
  };

  // ✅ Fetch exam + initialize
  useEffect(() => {
    setLoading(true)
    axios
      .get(`${baseUrl}/dynamic-exam/get?examId=${examId}`)
      .then((res) => {
        if (res.data.attempted) {
          Swal.fire({
            title: "Already Attempted",
            text: "You have already attempted this exam",
            icon: "info",
            confirmButtonText: "OK"
          }).then(() => {
            navigate(`/dynamic-exam/result/${examId}`);
          });
          return;
        }
        setExam(res.data);
        setLoading(false)

        // Set available subjects
        const subjects = [...new Set(res.data.questions.map(q => q.subject).filter(s => s))];
        setAvailableSubjects(subjects);
        const savedSubject = localStorage.getItem(`exam_${examId}_selectedSubject`);
        setSelectedSubject(savedSubject || subjects[0] || ""); // Default to first subject

        // Timer init
        if (res.data.timeLimit) {
          const savedTime = localStorage.getItem(`exam_${examId}_timeLeft`);
          setTimeLeft(savedTime ? parseInt(savedTime) : res.data.timeLimit * 60);
        }

        // Init answers
        const savedAnswers = localStorage.getItem(`exam_${examId}_answers`);
        const initial = {};
        res.data.questions.forEach((q) => {
          initial[q.id] = {
            answer: null,
            status: "unattempted",
            review: false,
          };
        });
        setAnswers(savedAnswers ? JSON.parse(savedAnswers) : initial);

        const savedIndex = localStorage.getItem(`exam_${examId}_currentQuestionIndex`);
        setCurrentQuestionIndex(savedIndex ? parseInt(savedIndex) : 0);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err)});
  }, [examId]);

  // Update filtered questions
  useEffect(() => {
    if (exam && exam.questions) {
      const filtered = selectedSubject ? exam.questions.filter(q => q.subject === selectedSubject) : exam.questions;
      setFilteredQuestions(filtered);
      setCurrentQuestionIndex(0); // Reset to first question when subject changes
    }
  }, [exam, selectedSubject]);

  // ✅ Prevent page reload and back button
  useEffect(() => {
    if (exam && !submit && examStarted) {
      const handleBeforeUnload = (event) => {
        event.preventDefault();
        event.returnValue = ''; // For some browsers like Chrome
      };

      const blockBackButton = () => {
        window.history.pushState(null, '', window.location.href);
        window.history.forward();
      };

      window.history.pushState(null, '', window.location.href);
      window.onpopstate = blockBackButton;
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.onpopstate = null;
      };
    }
  }, [exam, submit, examStarted]);

  // ✅ Timer logic (auto submit)
  useEffect(() => {
    if (!timeLeft || submit || !examStarted) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit(true);
          return 0;
        }

        const newTime = prev - 1;
        localStorage.setItem(`exam_${examId}_timeLeft`, newTime);
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [examStarted, submit]);

  // ✅ Format timer
  const formatTime = (time) => {
    const h = Math.floor(time / 3600);
    const m = Math.floor((time % 3600) / 60);
    const s = time % 60;
    return `${h}:${m < 10 ? "0" + m : m}:${s < 10 ? "0" + s : s}`;
  };

  // ✅ Handle subject change
  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
    localStorage.setItem(`exam_${examId}_selectedSubject`, subject);
  };

  // ✅ Get question number based on subject
  const getQuestionNumber = (subject, index, examType) => {
    if (examType === 'topic-wise') {
      return index + 1;
    }
    const sectionRanges = {
      "Physics": { start: 1, end: 45 },
      "Chemistry": { start: 46, end: 90 },
      "Biology": { start: 91, end: 180 },
    };

    const sectionKey = subject;
    const sectionRange = sectionRanges[sectionKey];

    if (sectionRange) {
      return sectionRange.start + index;
    }

    return index + 1; // Fallback
  };

  // ✅ Select answer
  const handleOptionChange = (qId, option) => {
    setAnswers((prev) => {
      const newAnswers = {
        ...prev,
        [qId]: {
          ...prev[qId],
          answer: option,
          status: "attempted",
        },
      };
      localStorage.setItem(`exam_${examId}_answers`, JSON.stringify(newAnswers));
      return newAnswers;
    });
  };

  // ✅ Mark for review
  const handleMarkForReview = (qId) => {
    setAnswers((prev) => {
      const newAnswers = {
        ...prev,
        [qId]: {
          ...prev[qId],
          review: !prev[qId]?.review,
        },
      };
      localStorage.setItem(`exam_${examId}_answers`, JSON.stringify(newAnswers));
      return newAnswers;
    });
  };

  // ✅ Submit exam
  const handleSubmit = async (isAutoSubmit) => {
     isAutoSubmit = isAutoSubmit === true; 
  if (submit || processing) return;
    if (!isAutoSubmit) {
      // Manual submit, show confirmation
      const willSubmit = await Swal.fire({
        title: "Are you sure?",
        text: `Make sure you have attempted all questions. \n\nClick "Re-Check" to check all questions \n\nClick "Proceed" if you have already checked and want to submit`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Proceed",
        cancelButtonText: "Re-check",
        confirmButtonColor: "#28a745",
        cancelButtonColor: "#6c757d"
      });

      if (!willSubmit.isConfirmed) {
        setSubmit(false); // Reset if cancelled
        return;
      }
    }

    setSubmit(true);
    try {
      setProcessing(true);

      const formattedAnswers = Object.keys(answers).map((qId) => {
        const question = exam.questions.find(q => q.id.toString() === qId);
        const selectedAnswer = answers[qId].answer;
        const selectedIndex = question?.options?.findIndex(option => option === selectedAnswer);

        return {
          questionId: qId,
          selectedOption: selectedAnswer === null ? null : selectedIndex >= 0 ? String(selectedIndex + 1) : selectedAnswer,
        };
      });

      const res = await api.post(`${baseUrl}/dynamic-exam/${exam.type === 'neet-flexi' ? 'submit-flexi' : 'submit'}`, {
        studentId: user.id,
        examId,
        answers: formattedAnswers,
      });

      if (!res.data || typeof res.data.score !== 'number') {
        throw new Error('Invalid response from server');
      }

      const redirect = () => {
        localStorage.removeItem(`exam_${examId}_timeLeft`);
        localStorage.removeItem(`exam_${examId}_answers`);
        localStorage.removeItem(`exam_${examId}_currentQuestionIndex`);
        localStorage.removeItem(`exam_${examId}_selectedSubject`);
        localStorage.removeItem(`exam_${examId}_started`);
        window.location.href = `/dynamic-exam/result/${examId}`;
      };

        redirect();

    } catch(err){
      toast.error(err.response?.data?.message || "Something went wrong");
    }finally {
      setProcessing(false);
      setSubmit(false)
    }
  };

  if (loading) return <h5 className="text-center mt-5">Loading Exam...</h5>;
  if (!loading && !exam) return (
    <div className="text-center mt-5">
      <h3>Exam not found</h3>
      <button
        className="btn btn-primary mt-3"
        onClick={() => navigate(`/student/${user?.email}`)}
      >
        Back to profile
      </button>
    </div>
  );
  if (exam?.attempted) {
  return (
    <div className="text-center mt-5">
      <h3>You have already attempted this exam</h3>
      <button
        className="btn btn-primary mt-3"
        onClick={() => navigate(`/result/${examId}`)}
      >
        View Result
      </button>
    </div>
  );
}


  return (
    <div className="container-fluid">
        {!examStarted ? (
      <div className="container mt-5">
        <div className="mt-2 p-3 border">
          <h5>Important Instructions:</h5>
            {exam.type === "neet-flexi" ?
              <FlexiExamInstruction/>
              :
              <TWPExamInstruction num={exam?.questions.length} time={exam.timeLimit}/>}

        </div>

        <div className="d-flex mt-3">
          <input
            type="checkbox"
            onChange={() => setRead(!read)}
          />
          <span className="ms-2">
            I have read the instructions
          </span>
        </div>

        <button
          className="btn btn-primary mt-3"
          onClick={startExam}
        >
          Start Exam
        </button>
      </div>
    ) : (
      <div>

      {/* ===== HEADER ===== */}
      <div className="mt-2 p-3" style={{ backgroundColor: "#8000D5" }}>
        <h5 className="text-center text-white">Monarch Institute</h5>
        <h5 className="text-center text-white">NEET - Online Exam</h5>
        <h5 className="text-center text-white">Exam Name : {exam.examName}</h5>
      </div>

      <div className="row">
        <div className="col-lg-3 col-sm-12">
          <p className="mt-3 ms-2">
            {timeLeft !== null && <span>
              <b>Time Left :</b> {formatTime(timeLeft)}
            </span>}
          </p>
        </div>
        <div className="mt-2 col-lg-9 col-sm-12" style={{ justifySelf: 'center' }}>
          {availableSubjects.length > 0 && availableSubjects.map((subject) => (
            <button
              key={subject}
              onClick={() => handleSubjectChange(subject)}
              className={`btn btn-sm m-1 ${subject === selectedSubject ? "btn-success" : "btn-secondary"}`}
            >
              {subject}
            </button>
          ))}
        </div>
      </div>

      <div className="row mt-3">

        {/* ===== LEFT PANEL ===== */}
        <div className="col-md-3">
          <h6>Note : </h6>
          <button className="btn btn-sm btn-success m-1">Answered</button>
          <button className="btn btn-sm btn-secondary m-1">Unanswered</button>
          <button className="btn btn-sm btn-warning m-1">Review</button><br />
          {filteredQuestions.map((q, index) => (
            <button
              key={q.id}
              style={{ paddingLeft: '6px', paddingRight: '6px', paddingTop: '5px', paddingBottom: '5px' }}
              className={`btn btn-sm m-1 btn-${
                answers[q.id]?.review
                  ? "warning"
                  : answers[q.id]?.status === "attempted"
                  ? "success"
                  : "secondary"
              }`}
              onClick={() => {
                setCurrentQuestionIndex(index);
                localStorage.setItem(`exam_${examId}_currentQuestionIndex`, index);
              }}
            >
              {getQuestionNumber(q.subject, index, exam.type)}
            </button>
          ))}
        </div>

        {/* ===== RIGHT PANEL ===== */}
        <div className="col-md-9">
          {filteredQuestions.length > 0 && (
            <div className="question">
              <span className="fw-bold fs-5">
                Q {getQuestionNumber(filteredQuestions[currentQuestionIndex].subject, currentQuestionIndex, exam.type)}
              </span>
              <div
                className="question-preview"
                dangerouslySetInnerHTML={{
                  __html: filteredQuestions[currentQuestionIndex].question,
                }}
              />
              {filteredQuestions[currentQuestionIndex].options &&
                filteredQuestions[currentQuestionIndex].options.length > 0 && (
                  <div>
                    {filteredQuestions[currentQuestionIndex].options.map((option, idx) => (
                      <div key={idx}>
                        <label className="mr-2">({idx + 1})</label>
                        <input
                          className="mr-2"
                          type="radio"
                          id={`option-${currentQuestionIndex}-${idx}`}
                          name={`question-${filteredQuestions[currentQuestionIndex].id}`}
                          value={option}
                          checked={answers[filteredQuestions[currentQuestionIndex].id]?.answer === option}
                          onChange={() => handleOptionChange(filteredQuestions[currentQuestionIndex].id, option)}
                        />
                        <label
                          htmlFor={`option-${currentQuestionIndex}-${idx}`}
                          dangerouslySetInnerHTML={{
                            __html: option,
                          }}
                        ></label>
                      </div>
                    ))}
                  </div>
                )}
              <button
                onClick={() =>
                  handleMarkForReview(
                    filteredQuestions[currentQuestionIndex].id
                  )
                }
                className="btn btn-info mt-2 btn-sm"
              >
                {answers[filteredQuestions[currentQuestionIndex].id]?.review
                  ? "Unmark for review"
                  : "Mark for Review"}
              </button>
            </div>
          )}

          {/* ===== NAVIGATION ===== */}
          <div className="mt-3">
            <button
              disabled={currentQuestionIndex === 0}
              onClick={() => {
                const newIndex = currentQuestionIndex - 1;
                setCurrentQuestionIndex(newIndex);
                localStorage.setItem(`exam_${examId}_currentQuestionIndex`, newIndex);
              }}
              className="btn btn-secondary btn-sm"
            >
              Previous
            </button>
            <button
              disabled={
                currentQuestionIndex === filteredQuestions.length - 1
              }
              onClick={() => {
                const newIndex = currentQuestionIndex + 1;
                setCurrentQuestionIndex(newIndex);
                localStorage.setItem(`exam_${examId}_currentQuestionIndex`, newIndex);
              }}
              className="btn btn-secondary ml-2 btn-sm"
            >
              Next
            </button>
          </div>

          {/* ===== SUBMIT ===== */}
          <button disabled={processing} className="mt-5 mb-3 btn btn-success btn-sm" onClick={()=>handleSubmit(false)}>
            {processing ? "Submitting..." : "Submit Exam"}
          </button>
        </div>
      </div>
      </div>
  )
}
</div>
);
};

export default DynamicExam;
